import {
  ReadOutreachDemo,
  ReadOutreachDemoAdmin,
  ReadOutreachDemoPublication,
  ReadOutreachDemoRun,
  StartOutreachDemo,
  UpdateOutreachDemoSettings,
} from '@tinct/core-api-models';
import { ApiClient } from '../api-client/api-client';

export class OutreachDemosEndpoint {
  constructor(private client: ApiClient) {}

  // Fully public endpoint (no auth, fixed response shape — no `fields`
  // projection). `id` is a demo id, or an organization id for links minted
  // before the demo entity. 404s for unknown ids and non-demo organizations.
  async getOutreachDemo(id: string): Promise<ReadOutreachDemo> {
    return this.client.getById<ReadOutreachDemo>(
      `/api/v1/public/outreach-demos/${id}`,
    );
  }

  // Super-admin only. The demo row: raw overrides, effective values, share.
  async getOutreachDemoAdmin(demoId: string): Promise<ReadOutreachDemoAdmin> {
    return this.client.getById<ReadOutreachDemoAdmin>(
      `/api-tools/v1/outreach/demos/${demoId}`,
    );
  }

  // Super-admin only. Partial update: omitted = unchanged, '' = back to the
  // default, value = override. Returns the demo as it now reads.
  async updateOutreachDemoSettings(
    demoId: string,
    data: UpdateOutreachDemoSettings,
  ): Promise<ReadOutreachDemoAdmin> {
    return this.client.patch<ReadOutreachDemoAdmin>(
      `/api-tools/v1/outreach/demos/${demoId}`,
      data,
    );
  }

  // Super-admin only. Publishes (or republishes) the demo as a static site:
  // index.html with the Open Graph meta + the embedded payload, the previewer
  // assets, the share card. Synchronous; 409 while the campaign does not exist
  // yet, 500 (status failed) when the build or the upload fails.
  async publishOutreachDemo(
    demoId: string,
  ): Promise<ReadOutreachDemoPublication> {
    return this.client.post<ReadOutreachDemoPublication>(
      `/api-tools/v1/outreach/demos/${demoId}/publish`,
    );
  }

  // Super-admin only. Returns immediately — the funnel runs in the background
  // for 15–30+ minutes, tracked by the returned job. Not org-scoped: the demo
  // organization is created by the run itself, so no org header is sent.
  async startOutreachDemo(
    data: StartOutreachDemo,
  ): Promise<ReadOutreachDemoRun> {
    return this.client.post<ReadOutreachDemoRun>(
      '/api-tools/v1/outreach/demos',
      data,
    );
  }

  // Super-admin only. Resumes a failed (or stuck) run in place from the step
  // it failed at — same job id, no new organization. 409 when the job is still
  // running or already completed.
  async resumeOutreachDemo(jobId: string): Promise<ReadOutreachDemoRun> {
    return this.client.post<ReadOutreachDemoRun>(
      `/api-tools/v1/outreach/demos/${jobId}/resume`,
    );
  }
}
